// Perfiles metrológicos de SSHH / Ambiental.
// Convención única: error = indicación del instrumento - valor corregido del patrón.

const SQRT3 = Math.sqrt(3);

export const ENVIRONMENTAL_TEMPLATES = [
  {
    code: 'ENV-AIR:anemometer:v1', revision: '1', profileKey: 'anemometer', subfamily: 'air_velocity',
    profileLabel: 'Anemómetro / termoanemómetro', title: 'Calibración de anemómetros y termoanemómetros',
    objectFallback: 'Anemómetro', unit: 'm/s', pointCount: 7, repeatCount: 1,
    functionKeys: ['air_velocity_reference'], defaultSetpoints: [2, 4, 6, 8, 10, 15, 20],
    method: 'Por comparación directa con anemómetro patrón certificado.',
    procedure: 'P-DT-PT-43-00 (Calibración de anemómetros)',
  },
  {
    code: 'ENV-LIGHT:luxmeter:v1', revision: '1', profileKey: 'luxmeter', subfamily: 'illuminance',
    profileLabel: 'Luxómetro', title: 'Calibración de luxómetros', objectFallback: 'Luxómetro',
    unit: 'lx', pointCount: 7, repeatCount: 1,
    functionKeys: ['illuminance_reference'], defaultSetpoints: [100, 500, 1000, 2000, 5000, 10000, 20000],
    method: 'Por comparación directa con luxómetro patrón certificado bajo una fuente estabilizada.',
    procedure: 'P-DT-PT-44-00 (Calibración de luxómetros)',
  },
  {
    code: 'ENV-SOUND:sound_level_meter:v1', revision: '1', profileKey: 'sound_level_meter', subfamily: 'sound_level',
    profileLabel: 'Decibelímetro / sonómetro', title: 'Calibración de decibelímetros y sonómetros',
    objectFallback: 'Decibelímetro', unit: 'dB', pointCount: 2, repeatCount: 5,
    functionKeys: ['acoustic_calibrator'], defaultSetpoints: [94, 114],
    method: 'Por aplicación directa de niveles acústicos con calibrador patrón certificado.',
    procedure: 'P-DT-PT-33-00 (Calibración de decibelímetros)',
  },
];

const ENVIRONMENT_READINGS = [
  { key: 'start', label: 'Inicio', temp_c: null, pressure_hpa: null, humidity_rh: null },
  { key: 'middle', label: 'Mitad', temp_c: null, pressure_hpa: null, humidity_rh: null },
  { key: 'end', label: 'Fin', temp_c: null, pressure_hpa: null, humidity_rh: null },
];

export const DEFAULT_ENVIRONMENTAL_FAMILY_DATA = {
  profile_key: 'anemometer', profile_label: 'Anemómetro / termoanemómetro',
  method: '', procedure: '', tolerance_mode: 'absolute', decision_rule: 'simple',
  standard_configuration: { references: [] },
  standard_budget: {
    drift: 0, repeatability_limit: 0, positioning: 0, field_uniformity: 0,
    source_stability: 0, environmental_influence: 0, frequency_response: 0, other: 0,
  },
  environment_readings: ENVIRONMENT_READINGS,
  test_configuration: {
    medium: 'Aire', probe_orientation: 'Alineada con el flujo', duct_section: '',
    light_source: '', distance_mm: null, stabilization_seconds: null,
    acoustic_frequency_hz: 1000, frequency_weighting: 'A', time_weighting: 'FAST',
    microphone_adapter: '', adjustment_performed: false,
  },
};

export function resolveEnvironmentalTemplate(templateCode) {
  return ENVIRONMENTAL_TEMPLATES.find((item) => item.code === templateCode) || ENVIRONMENTAL_TEMPLATES[0];
}

export function defaultEnvironmentalFamilyData(templateCode) {
  const template = resolveEnvironmentalTemplate(templateCode);
  return {
    ...DEFAULT_ENVIRONMENTAL_FAMILY_DATA,
    profile_key: template.profileKey,
    profile_label: template.profileLabel,
    method: template.method,
    procedure: template.procedure,
    standard_budget: { ...DEFAULT_ENVIRONMENTAL_FAMILY_DATA.standard_budget },
    standard_configuration: { references: [] },
    environment_readings: ENVIRONMENT_READINGS.map((item) => ({ ...item })),
    test_configuration: { ...DEFAULT_ENVIRONMENTAL_FAMILY_DATA.test_configuration },
  };
}

export function mergeEnvironmentalFamilyData(value = {}, templateCode = null) {
  const input = value && typeof value === 'object' ? value : {};
  const inferredCode = templateCode || input.template_code || ENVIRONMENTAL_TEMPLATES.find((item) => item.profileKey === input.profile_key)?.code;
  const defaults = defaultEnvironmentalFamilyData(inferredCode);
  const readings = Array.isArray(input.environment_readings) ? input.environment_readings : [];
  const references = Array.isArray(input?.standard_configuration?.references)
    ? input.standard_configuration.references
    : input?.standard_configuration?.reference ? [input.standard_configuration.reference] : [];
  return {
    ...defaults,
    ...input,
    standard_budget: { ...defaults.standard_budget, ...(input.standard_budget || {}) },
    test_configuration: { ...defaults.test_configuration, ...(input.test_configuration || {}) },
    standard_configuration: { ...(input.standard_configuration || {}), references },
    environment_readings: defaults.environment_readings.map((fallback, index) => ({
      ...fallback,
      ...(readings[index] || readings.find((item) => item?.key === fallback.key) || {}),
    })),
  };
}

export function buildEnvironmentalBlocks(templateCode) {
  const template = resolveEnvironmentalTemplate(templateCode);
  return [{
    block_key: template.subfamily,
    display_name: template.profileLabel,
    unit: `[${template.unit}]`,
    applicable: true,
    point_count: template.pointCount,
    sort_order: 1,
    mode: 'comparison',
  }];
}

export function environmentalReadingPairs(templateCode) {
  const template = resolveEnvironmentalTemplate(templateCode);
  return Array.from({ length: template.repeatCount }, (_, index) => ({
    key: `reading_${index + 1}`,
    direction: 'NA',
    seriesNo: index + 1,
    label: template.repeatCount === 1 ? 'Comparación' : `Lectura ${index + 1}`,
    patternField: `reading_${index + 1}_pattern`,
    instrumentField: `reading_${index + 1}_instrument`,
  }));
}

export function finiteOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function normalizedUnit(value) {
  return String(value || '').trim().toLowerCase().replace(/μ/g, 'µ').replace(/º/g, '°').replace(/\s+/g, '');
}

export function environmentalToCanonical(value, unit, templateCode) {
  const numeric = finiteOrNull(value);
  if (numeric === null) return null;
  const template = resolveEnvironmentalTemplate(templateCode);
  const source = normalizedUnit(unit || template.unit);
  if (template.profileKey === 'anemometer') {
    if (['m/s', 'mps', 'mseg', 'm/seg'].includes(source)) return numeric;
    if (['km/h', 'kmh'].includes(source)) return numeric / 3.6;
    if (['ft/min', 'fpm'].includes(source)) return numeric * 0.00508;
    throw new Error(`La unidad ${unit || '(sin unidad)'} no es compatible con velocidad de aire.`);
  }
  if (template.profileKey === 'luxmeter') {
    if (['lx', 'lux'].includes(source)) return numeric;
    if (['klx', 'klux'].includes(source)) return numeric * 1000;
    throw new Error(`La unidad ${unit || '(sin unidad)'} no es compatible con iluminancia.`);
  }
  if (['db', 'dba', 'dbc'].includes(source)) return numeric;
  throw new Error(`La unidad ${unit || '(sin unidad)'} no es compatible con nivel sonoro.`);
}

export function suggestEnvironmentalSetpoints(rangeMin, rangeMax, templateCode) {
  const template = resolveEnvironmentalTemplate(templateCode);
  const min = finiteOrNull(rangeMin);
  const max = finiteOrNull(rangeMax);
  const filtered = template.defaultSetpoints.filter((value) => (min === null || value >= min) && (max === null || value <= max));
  return filtered.length ? filtered : [...template.defaultSetpoints];
}

function mean(values = []) {
  const finite = values.map(finiteOrNull).filter((value) => value !== null);
  return finite.length ? finite.reduce((sum, value) => sum + value, 0) / finite.length : null;
}

function sampleStandardDeviation(values = []) {
  const finite = values.map(finiteOrNull).filter((value) => value !== null);
  if (finite.length < 2) return 0;
  const average = mean(finite);
  return Math.sqrt(finite.reduce((sum, value) => sum + ((value - average) ** 2), 0) / (finite.length - 1));
}

function rss(values = []) {
  return Math.sqrt(values.reduce((sum, value) => sum + ((finiteOrNull(value) ?? 0) ** 2), 0));
}

function evaluateSnapshot(value, snapshot = {}) {
  const raw = finiteOrNull(value);
  if (raw === null) return { valid: false, reason: 'Lectura patrón vacía.' };
  const capability = snapshot.capability || {};
  const metadata = capability.metadata || {};
  const rangeMin = finiteOrNull(capability.range_min);
  const rangeMax = finiteOrNull(capability.range_max);
  if (rangeMin !== null && raw < rangeMin) return { valid: false, reason: 'La lectura está por debajo del alcance del patrón.' };
  if (rangeMax !== null && raw > rangeMax) return { valid: false, reason: 'La lectura supera el alcance del patrón.' };
  const results = Array.isArray(snapshot.results) ? snapshot.results : [];
  const coverageFactor = finiteOrNull(snapshot?.certificate?.coverage_factor) || 2;
  const constantU = finiteOrNull(metadata.certified_uncertainty);
  const mode = metadata.reference_input_mode || results[0]?.metadata?.reference_input_mode || 'raw_indication';
  if (mode === 'corrected_value') {
    const uncertaintyExpanded = constantU ?? Math.max(...results.map((item) => finiteOrNull(item.uncertainty_expanded)).filter((item) => item !== null), -Infinity);
    if (!Number.isFinite(uncertaintyExpanded)) return { valid: false, reason: 'El patrón no tiene incertidumbre documentada.' };
    return { valid: true, corrected_value: raw, correction: 0, standard_uncertainty: Math.abs(uncertaintyExpanded) / coverageFactor, uncertainty_expanded: Math.abs(uncertaintyExpanded), coverage_factor: coverageFactor };
  }
  const tolerance = Math.max(Math.abs(finiteOrNull(capability.resolution) || 0) / 2, 1e-9);
  const exact = results.find((item) => {
    const axis = finiteOrNull(item.indicated_value) ?? finiteOrNull(item.reference_value);
    return axis !== null && Math.abs(axis - raw) <= tolerance;
  });
  if (!exact) return { valid: false, reason: 'La lectura no coincide con un punto certificado del patrón.' };
  const correction = finiteOrNull(exact.correction) ?? ((finiteOrNull(exact.reference_value) ?? raw) - (finiteOrNull(exact.indicated_value) ?? raw));
  const uncertaintyExpanded = finiteOrNull(exact.uncertainty_expanded) ?? constantU;
  const k = finiteOrNull(exact.coverage_factor) || coverageFactor;
  if (uncertaintyExpanded === null || k <= 0) return { valid: false, reason: 'El punto certificado no tiene incertidumbre utilizable.' };
  return { valid: true, corrected_value: raw + correction, correction, standard_uncertainty: Math.abs(uncertaintyExpanded) / k, uncertainty_expanded: Math.abs(uncertaintyExpanded), coverage_factor: k };
}

export function evaluateEnvironmentalReference(value, session = {}) {
  const data = mergeEnvironmentalFamilyData(session.family_data, session.template_code);
  const references = data.standard_configuration?.references || [];
  if (!references.length) return { valid: false, reason: 'No hay patrón fijado en la sesión.' };
  const attempts = references.map((snapshot) => evaluateSnapshot(value, snapshot));
  return attempts.find((attempt) => attempt.valid) || { valid: false, reason: attempts.map((attempt) => attempt.reason).filter(Boolean).join(' ') || 'El patrón no cubre la lectura.' };
}

export function computeEnvironmentalPointMetrics(row = {}, session = {}) {
  const pairs = environmentalReadingPairs(session.template_code).map((pair) => ({
    ...pair,
    pattern: finiteOrNull(row[pair.patternField]),
    instrument: finiteOrNull(row[pair.instrumentField]),
  })).filter((pair) => pair.pattern !== null && pair.instrument !== null);
  if (!pairs.length) return null;
  const evaluations = pairs.map((pair) => evaluateEnvironmentalReference(pair.pattern, session));
  const invalid = evaluations.find((item) => !item.valid);
  if (invalid) return { traceability_issue: invalid.reason };
  const corrected = evaluations.map((item) => item.corrected_value);
  const indications = pairs.map((item) => item.instrument);
  const patternMean = mean(corrected);
  const instrumentMean = mean(indications);
  const errorAbs = instrumentMean - patternMean;
  const errorPct = patternMean === 0 ? null : errorAbs / patternMean * 100;
  const repeatability = sampleStandardDeviation(indications);
  const referenceRepeatability = sampleStandardDeviation(corrected);
  const count = Math.max(1, indications.length);
  const data = mergeEnvironmentalFamilyData(session.family_data, session.template_code);
  const budget = data.standard_budget || {};
  const uStandard = Math.max(...evaluations.map((item) => item.standard_uncertainty), 0);
  const uResolution = Math.abs(finiteOrNull(session.instrument_resolution) || 0) / Math.sqrt(12);
  const uRepeatability = rss([
    repeatability / Math.sqrt(count),
    referenceRepeatability / Math.sqrt(count),
    Math.abs(finiteOrNull(budget.repeatability_limit) || 0) / SQRT3,
  ]);
  const uDrift = Math.abs(finiteOrNull(budget.drift) || 0) / SQRT3;
  const uPositioning = Math.abs(finiteOrNull(budget.positioning) || 0) / SQRT3;
  const uField = Math.abs(finiteOrNull(budget.field_uniformity) || 0) / SQRT3;
  const uStability = Math.abs(finiteOrNull(budget.source_stability) || 0) / SQRT3;
  const uEnvironment = Math.abs(finiteOrNull(budget.environmental_influence) || 0) / SQRT3;
  const uFrequency = Math.abs(finiteOrNull(budget.frequency_response) || 0) / SQRT3;
  const uOther = rss([uPositioning, uField, uStability, uEnvironment, uFrequency, Math.abs(finiteOrNull(budget.other) || 0) / SQRT3]);
  const uc = rss([uStandard, uDrift, uResolution, uRepeatability, uOther]);
  const k = 2;
  const uExpanded = k * uc;
  const toleranceValue = finiteOrNull(session.instrument_class);
  const tolerance = toleranceValue === null ? null : data.tolerance_mode === 'percent' ? Math.abs(patternMean) * toleranceValue / 100 : Math.abs(toleranceValue);
  const inTolerance = tolerance === null ? null : data.decision_rule === 'guard_band' ? Math.abs(errorAbs) + uExpanded <= tolerance : Math.abs(errorAbs) <= tolerance;
  return {
    pattern_mean: patternMean,
    instrument_mean: instrumentMean,
    error_abs: errorAbs,
    error_pct: errorPct,
    hysteresis: null,
    repeatability,
    u_standard: uStandard,
    u_drift: uDrift,
    u_resolution: uResolution,
    u_repeatability: uRepeatability,
    u_temp: uEnvironment,
    u_other: uOther,
    uc,
    k,
    u_expanded: uExpanded,
    u_reported: Math.max(uExpanded, Math.abs(Number(session.instrument_resolution) || 0)), // piso por resolucion del instrumento (QA 2026-09-06)
    in_tolerance: inTolerance,
    uncertainty_components: {
      reference_repeatability: referenceRepeatability,
      positioning: uPositioning,
      field_uniformity: uField,
      source_stability: uStability,
      environmental_influence: uEnvironment,
      frequency_response: uFrequency,
      tolerance,
      decision_rule: data.decision_rule,
    },
  };
}
